/**
 * THE CLIENTS PACKAGE'S PURE RULES (fulfillment wave, P5). Everything a Clients screen says
 * that can be decided without a request lives here, so each rule has one test and the pages
 * only render its answers: the week words, the health order, the billing sums and the money
 * and date words. The client page's own rules (its sections, Activity, promises) are
 * `clientPageModel.ts`.
 *
 * The rules are the concept's (`crm-design/decisions/FULFILLMENT-CONCEPT-2026-09-28-night.md`
 * sections 4.4, 4.5, 4.7) and the wave's common contract, section 4; the words are Ben's.
 *
 * The numbers an installation chooses (program length, renewal week, the onboarding steps) are
 * the configuration's (`FulfillmentConfiguration`, DESIGN.md Part 1 priority 7).
 */
import type { FulfillmentConfiguration } from '@ofmos/shared/fulfillment-configuration';
import { FULFILLMENT } from '../shared/configuration';

// --- week -------------------------------------------------------------------

const DAY_MS = 86_400_000;

function dayNumber(iso: string): number | null {
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) return null;
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  return Date.UTC(year, month - 1, day) / DAY_MS;
}

/** Today as a `YYYY-MM-DD` key in the viewer's own calendar, which is what a DATE field means. */
export function localDayKey(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${String(now.getFullYear())}-${month}-${day}`;
}

/** Whole days from `from` to `to`, both `YYYY-MM-DD`; null when either is not a day. */
export function daysBetween(from: string, to: string): number | null {
  const start = dayNumber(from);
  const end = dayNumber(to);
  return start === null || end === null ? null : end - start;
}

/**
 * THE WEEK (common contract section 4): `floor((today - contract_start_date) / 7) + 1`,
 * capped at `contract_weeks`, or at the configured program length when the client has none (16
 * in OFMOS), nothing without a start date. A start in the future is week 1 rather than week 0
 * or a negative week: the client has joined, the clock has not started.
 */
export function weekOf(
  start: string | null,
  weeks: number | null,
  today: string = localDayKey(),
  config: FulfillmentConfiguration = FULFILLMENT,
): { readonly week: number; readonly of: number } | null {
  if (start === null) return null;
  const elapsed = daysBetween(start, today);
  if (elapsed === null) return null;
  const of = weeks !== null && weeks > 0 ? Math.floor(weeks) : config.program.weeks;
  const week = Math.min(of, Math.max(1, Math.floor(elapsed / 7) + 1));
  return { week, of };
}

/** "week 10 of 16", or null without a start date (the list then says "No start date"). */
export function weekWords(
  start: string | null,
  weeks: number | null,
  today: string = localDayKey(),
  config: FulfillmentConfiguration = FULFILLMENT,
): string | null {
  const found = weekOf(start, weeks, today, config);
  return found === null ? null : `week ${String(found.week)} of ${String(found.of)}`;
}

/**
 * THE PROGRAM WEEK WITH ITS OVERRUN (redesign register D9: "clients past week 16 read "past
 * week 16""). `weekOf` caps at the contract weeks and stays as it is, because the client page
 * reads it; this keeps that capped week for the bar and the Program week groups (Q-CL-7, "the
 * capped week") and adds the uncapped week the clock has reached, so the list can say that a
 * program ran past its last week and when the renewal status starts to show.
 */
export interface ProgramWeek {
  /** The week capped at the contract weeks, never below 1. */
  readonly week: number;
  readonly of: number;
  /** The week the clock has reached, uncapped, never below 1. */
  readonly reached: number;
  /** True once `reached` is past `of`. */
  readonly past: boolean;
}

export function programWeekOf(
  start: string | null,
  weeks: number | null,
  today: string = localDayKey(),
  config: FulfillmentConfiguration = FULFILLMENT,
): ProgramWeek | null {
  const found = weekOf(start, weeks, today, config);
  const elapsed = start === null ? null : daysBetween(start, today);
  if (found === null || elapsed === null) return null;
  const reached = Math.max(1, Math.floor(elapsed / 7) + 1);
  return { week: found.week, of: found.of, reached, past: reached > found.of };
}

/** "Week 12 of 16", or "Past week 16" once the program has run past its last week (D9). */
export function programWords(program: ProgramWeek): string {
  return program.past
    ? `Past week ${String(program.of)}`
    : `Week ${String(program.week)} of ${String(program.of)}`;
}

/**
 * Register D6: "renewal status from week 13 on the client page and in the list", the week the
 * configuration sets (G15; `program.renewalFromWeek`, 13 in OFMOS).
 */
export const RENEWAL_FROM_WEEK = FULFILLMENT.program.renewalFromWeek;

/** Whether the renewal status shows beside the week: from that week on, past the end included. */
export function showsRenewal(
  program: ProgramWeek | null,
  config: FulfillmentConfiguration = FULFILLMENT,
): boolean {
  return program !== null && program.reached >= config.program.renewalFromWeek;
}

// --- health -----------------------------------------------------------------

export type HealthColour = 'red' | 'yellow' | 'green';

/** The option LABEL is the key the browser has (`dashboard/queries.ts`, optionIdByLabel). */
export function healthColourOf(label: string | null | undefined): HealthColour | null {
  switch (label?.trim().toLowerCase()) {
    case 'red':
      return 'red';
    case 'yellow':
      return 'yellow';
    case 'green':
      return 'green';
    default:
      return null;
  }
}

const HEALTH_RANK: Readonly<Record<HealthColour, number>> = { red: 0, yellow: 1, green: 2 };

export interface HealthSortable {
  readonly health: HealthColour | null;
  /** `YYYY-MM-DD` or null. */
  readonly healthSince: string | null;
  readonly name: string;
}

/**
 * RED, THEN YELLOW, THEN GREEN, AND WITHIN A COLOUR THE OLDEST `health_since` FIRST (common
 * contract section 4). A row with no colour yet goes last, and a row with no since-date goes
 * after the dated rows of its colour: an unknown age is not the oldest age. The name breaks a
 * tie so the order is the same on every render.
 */
export function compareByHealth(left: HealthSortable, right: HealthSortable): number {
  const rank = (row: HealthSortable) => (row.health === null ? 3 : HEALTH_RANK[row.health]);
  if (rank(left) !== rank(right)) return rank(left) - rank(right);
  if (left.healthSince !== right.healthSince) {
    if (left.healthSince === null) return 1;
    if (right.healthSince === null) return -1;
    return left.healthSince < right.healthSince ? -1 : 1;
  }
  return left.name.localeCompare(right.name);
}

// --- names and words --------------------------------------------------------

/** The first word of a name; a first-name field wins when it is filled. */
export function firstNameOf(name: string | null | undefined, firstName?: string | null): string {
  const given = firstName?.trim();
  if (given !== undefined && given !== '') return given;
  return name?.trim().split(/\s+/)[0] ?? '';
}

/** "Mara & Iggy": the people line under a client's name in the list. */
export function joinFirstNames(names: readonly string[]): string {
  return names.filter((name) => name !== '').join(' & ');
}

/** `30.06.2026`: the date shape in Ben's own lines ("started 30.06.2026"). */
export function formatDay(value: string | null | undefined): string | null {
  if (typeof value !== 'string' || value.length < 10) return null;
  const [year, month, day] = value.slice(0, 10).split('-');
  if (year === undefined || month === undefined || day === undefined) return null;
  return `${day}.${month}.${year}`;
}

/** The newest of several instants (ISO strings), ignoring the empty ones. */
export function newestOf(values: readonly (string | null | undefined)[]): string | null {
  let newest: string | null = null;
  for (const value of values) {
    if (typeof value !== 'string' || value === '') continue;
    if (newest === null || Date.parse(value) > Date.parse(newest)) newest = value;
  }
  return newest;
}

/** The first sentence of a paragraph, for a key fact that must stay one line of thought. */
export function firstSentence(text: string | null | undefined): string | null {
  const trimmed = text?.trim();
  if (trimmed === undefined || trimmed === '') return null;
  const match = trimmed.match(/^[\s\S]*?[.!?](?=\s|$)/);
  return (match?.[0] ?? trimmed).replace(/\s+/g, ' ').trim();
}

// --- money ------------------------------------------------------------------

export interface Money {
  readonly amountMicros: number;
  readonly currencyCode: string;
}

const MICROS = 1_000_000;

/** "EUR 8,000", "EUR 2,500.50": the code first, as Ben writes it, never the symbol. */
export function formatMoney(value: Money): string {
  const units = value.amountMicros / MICROS;
  const whole = Number.isInteger(units);
  const figure = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(units);
  return `${value.currencyCode} ${figure}`;
}

export function isMoney(value: unknown): value is Money {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Money).amountMicros === 'number' &&
    typeof (value as Money).currencyCode === 'string'
  );
}

// --- billing ----------------------------------------------------------------

export type PaymentWord = 'paid' | 'open' | 'late';

export interface PaymentFact {
  readonly id: string;
  readonly dealId: string | null;
  readonly amount: Money | null;
  /** `YYYY-MM-DD` or null. */
  readonly dueDate: string | null;
  readonly paidAt: string | null;
  /** The status option's LABEL. */
  readonly status: string | null;
}

/** Refunded and written-off rows are neither paid nor owed. */
const SETTLED_OUT = new Set(['refunded', 'written off']);

/**
 * PAID, OPEN OR LATE, the three words Ben's billing list uses. Paid is the status or a paid
 * date; a settled-out row (refunded, written off) answers null and is left out of the list's
 * sums; everything else is open, and late once its due day has passed.
 */
export function paymentWord(
  payment: PaymentFact,
  today: string = localDayKey(),
): PaymentWord | null {
  const status = payment.status?.trim().toLowerCase() ?? '';
  if (status === 'paid' || (payment.paidAt !== null && status === '')) return 'paid';
  if (SETTLED_OUT.has(status)) return null;
  if (payment.dueDate !== null && payment.dueDate.slice(0, 10) < today) return 'late';
  return 'open';
}

export interface DealBilling {
  readonly paid: Money | null;
  readonly open: Money | null;
  /** The earliest unpaid payment, which is what "next payment" names. */
  readonly next: PaymentFact | null;
}

function sum(payments: readonly PaymentFact[]): Money | null {
  const withAmount = payments.filter((payment) => payment.amount !== null);
  const first = withAmount[0]?.amount;
  if (first === undefined || first === null) return null;
  return {
    currencyCode: first.currencyCode,
    amountMicros: withAmount.reduce(
      (total, payment) => total + (payment.amount?.amountMicros ?? 0),
      0,
    ),
  };
}

/** Paid, open and the next payment of ONE deal's payments. */
export function dealBilling(
  payments: readonly PaymentFact[],
  today: string = localDayKey(),
): DealBilling {
  const paid = payments.filter((payment) => paymentWord(payment, today) === 'paid');
  const unpaid = payments.filter((payment) => {
    const word = paymentWord(payment, today);
    return word === 'open' || word === 'late';
  });
  const next =
    unpaid
      .filter((payment) => payment.dueDate !== null)
      .toSorted((left, right) => (left.dueDate ?? '').localeCompare(right.dueDate ?? ''))[0] ??
    unpaid[0] ??
    null;
  return { paid: sum(paid), open: sum(unpaid), next };
}

/** The list's "Open" column: every unpaid payment of every deal of one agency. */
export function openTotal(
  payments: readonly PaymentFact[],
  today: string = localDayKey(),
): Money | null {
  return dealBilling(payments, today).open;
}

// --- onboarding board -------------------------------------------------------

/** The configured onboarding steps' field names, in order (nine in OFMOS, register D5). */
export const ONBOARDING_STEP_FIELDS: readonly string[] = FULFILLMENT.onboardingSteps.map(
  (step) => step.field,
);

export const ONBOARDING_PHASES: readonly string[] = ['pre-onboarding', 'activation'];

/**
 * WHO IS ON THE ONBOARDING BOARD (common contract section 4): phase Pre-onboarding or
 * Activation, OR a contract start within the last 14 days; never a churned client; and off
 * the board once every step that exists has a date.
 */
export function isOnBoard(input: {
  readonly phase: string | null;
  readonly start: string | null;
  readonly churned: boolean;
  readonly stepDates: readonly (string | null)[];
  readonly today?: string;
}): boolean {
  if (input.churned) return false;
  if (input.stepDates.length > 0 && input.stepDates.every((date) => date !== null)) return false;
  const phase = input.phase?.trim().toLowerCase() ?? '';
  if (ONBOARDING_PHASES.includes(phase)) return true;
  if (input.start === null) return false;
  const since = daysBetween(input.start, input.today ?? localDayKey());
  return since !== null && since >= 0 && since <= 14;
}

// --- owner segment ----------------------------------------------------------

export const OWNER_STORAGE_KEY = 'fulfillment.owner';
export const ALL_OWNERS = 'all';

export interface OwnerChoice {
  readonly id: string;
  readonly label: string;
}

/**
 * "All" plus one segment per coach who owns at least one active client (common contract
 * section 4), the coach with the most clients first, then alphabetically. The caller passes
 * one entry per client, so the number of entries per coach is the count. No person is named
 * in the order (coordinator, 4 Oct: the CRM is a platform and OFMOS is its configuration).
 */
export function ownerSegments(owners: readonly OwnerChoice[]): readonly OwnerChoice[] {
  const counts = new Map<string, number>();
  for (const owner of owners) counts.set(owner.id, (counts.get(owner.id) ?? 0) + 1);
  const unique = [...new Map(owners.map((owner) => [owner.id, owner])).values()];
  return [
    { id: ALL_OWNERS, label: 'All' },
    ...unique.toSorted(
      (left, right) =>
        (counts.get(right.id) ?? 0) - (counts.get(left.id) ?? 0) ||
        left.label.localeCompare(right.label),
    ),
  ];
}

// --- check-in ---------------------------------------------------------------

/**
 * "+137 %", the growth figure on Ben's check-in card, from this week's number and the one
 * before it. Null when either is missing or the base is zero: growth from nothing is not a
 * percentage, and printing "+Infinity %" would be a figure nobody can act on.
 */
export function percentChange(now: number | null, before: number | null): string | null {
  if (now === null || before === null || before === 0) return null;
  const change = Math.round(((now - before) / Math.abs(before)) * 100);
  return `${change > 0 ? '+' : ''}${String(change)} %`;
}

/** A stored percentage field ("137" or 1.37 is not guessed: the field holds whole percent). */
export function percentWords(value: number | null): string | null {
  if (value === null) return null;
  const rounded = Math.round(value);
  return `${rounded > 0 ? '+' : ''}${String(rounded)} %`;
}

// --- tasks ------------------------------------------------------------------

export type TaskKind = 'open_item' | 'follow_up';

/** A task without a kind counts as an open item (common contract section 4). */
export function taskKindOf(task: object): TaskKind {
  const kind = (task as { readonly kind?: unknown }).kind;
  return kind === 'follow_up' ? 'follow_up' : 'open_item';
}

/** "3 days late", "due today", "due in 2 days": Ben's deadline words for a task row. */
export function dueWords(dueDate: string | null, today: string = localDayKey()): string | null {
  if (dueDate === null) return null;
  const days = daysBetween(today, dueDate);
  if (days === null) return null;
  if (days === 0) return 'due today';
  if (days < 0) return days === -1 ? '1 day late' : `${String(-days)} days late`;
  return days === 1 ? 'due tomorrow' : `due in ${String(days)} days`;
}
